/**
 * Éditeur de layout embarqué — iframe vers box-positionner (S-9a).
 * Le JSON est chargé/sauvé via postMessage, jamais téléchargé.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { unwrapBoxes, unwrapWidthFactor } from '../../utils/adventureLayoutFormat';

const IFRAME_SRC = '/tools/box-positionner.html?embed=1';

export default function BoxPositionnerEditor({
  open,
  onClose,
  title,
  layout,
  backgroundImageUrl,
  widthFactor: initialWidthFactor = 1,
  onSaveDraft,
  onPublish,
  saving = false,
  publishing = false,
  isPublished = false,
}) {
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [widthFactor, setWidthFactor] = useState(initialWidthFactor);
  const pendingLoadRef = useRef(false);

  const pushLayoutToIframe = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        type: 'dicto:load',
        boxes: unwrapBoxes(layout),
        imageUrl: backgroundImageUrl || null,
      },
      window.location.origin
    );
    pendingLoadRef.current = false;
  }, [layout, backgroundImageUrl]);

  useEffect(() => {
    if (!open) {
      setReady(false);
      setLoadError(null);
      pendingLoadRef.current = true;
      return;
    }
    setWidthFactor(initialWidthFactor);
  }, [open, initialWidthFactor]);

  useEffect(() => {
    if (open && ready && pendingLoadRef.current) {
      pushLayoutToIframe();
    }
  }, [open, ready, pushLayoutToIframe]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow || event.origin !== window.location.origin) return;
      const msg = event.data;
      if (!msg || typeof msg.type !== 'string' || !msg.type.startsWith('dicto:')) return;

      if (msg.type === 'dicto:ready') {
        setReady(true);
        pendingLoadRef.current = true;
        pushLayoutToIframe();
      }

      if (msg.type === 'dicto:loaded') {
        setLoadError(null);
      }

      if (msg.type === 'dicto:error') {
        setLoadError(msg.message || 'Erreur de chargement dans l\'éditeur');
      }

      if (msg.type === 'dicto:export' && Array.isArray(msg.boxes)) {
        onSaveDraft?.(msg.boxes, widthFactor);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onSaveDraft, pushLayoutToIframe, widthFactor]);

  const requestExport = () => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'dicto:request-export' }, window.location.origin);
  };

  const boxCount = unwrapBoxes(layout).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '92vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        {title}
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          {boxCount} boîte(s) en BDD · facteur toile {unwrapWidthFactor(layout) || initialWidthFactor}
          {isPublished ? ' · publiée' : ' · brouillon'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loadError ? (
          <Alert severity="warning" sx={{ m: 2 }} onClose={() => setLoadError(null)}>
            {loadError}
            {!backgroundImageUrl ? ' Chargez une image manuellement dans l\'éditeur si besoin.' : null}
          </Alert>
        ) : null}

        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Facteur largeur (D-10)"
            type="number"
            size="small"
            inputProps={{ min: 1, max: 3, step: 0.01 }}
            value={widthFactor}
            onChange={(e) => setWidthFactor(parseFloat(e.target.value) || 1)}
            sx={{ width: 180 }}
          />
          <Typography variant="body2" color="text.secondary">
            1 = toile fixe · 1,33 = Forêt défilante
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: '#0f172a' }}>
          {!ready ? (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : null}
          <iframe
            ref={iframeRef}
            title="Box positionner"
            src={IFRAME_SRC}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving || publishing}>
          Fermer
        </Button>
        <Button variant="outlined" onClick={pushLayoutToIframe} disabled={!ready}>
          Recharger depuis la BDD
        </Button>
        <Button variant="contained" onClick={requestExport} disabled={!ready || saving}>
          {saving ? 'Enregistrement…' : 'Sauvegarder brouillon'}
        </Button>
        {onPublish ? (
          <Button
            variant="contained"
            color="success"
            onClick={onPublish}
            disabled={publishing}
          >
            {publishing ? 'Publication…' : 'Publier la présentation'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
